import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import type { ValidationError } from "@/lib/api";
import { colors, fonts } from "@/lib/theme";

/**
 * Status of a single error card. Visual states defined by Validation-Flow-v2:
 *
 *   active              Default — amber/rose tinted strip with action chips +
 *                       red-outlined Dismiss pill.
 *   dismissed           RN tapped Dismiss → green "Acknowledged · dismissed".
 *   narrative-resolved  Reserved — RN's narrative edit cleared the error on
 *                       re-validation. Renders "Resolved via narrative".
 *   diagnosis-resolved  Reserved — RN's diagnosis edit cleared the error on
 *                       re-validation. Renders "Resolved via diagnosis".
 *
 * The orchestrator currently emits only "active" | "dismissed". The two
 * "resolved-via-…" states live in the type so the design system is complete;
 * wiring them is a separate task.
 */
export type ErrorCardStatus = "active" | "dismissed" | "narrative-resolved" | "diagnosis-resolved";

interface Props {
    error: ValidationError;
    status: ErrorCardStatus;
    /** Scroll the RN down to the Narrative chapter (Ch02). */
    onAddNarrative: () => void;
    /** Scroll the RN down to the Diagnoses chapter (Ch03). */
    onAddDiagnosis: () => void;
    /** Dismiss — orchestrator fires DELETE on the server. */
    onAcknowledge: () => void;
}

const ROSE = "#E11D48";
const ROSE_BG = "rgba(225,29,72,0.06)";
const ROSE_BORDER = "rgba(225,29,72,0.22)";
const ROSE_PILL_BG = "rgba(225,29,72,0.16)";
const WARN_DK = "#B45309";
const WARN_PILL_BG = "rgba(245,158,11,0.18)";
const OK = "#10B981";
const OK_DK = "#047857";
const OK_BG = "rgba(16,185,129,0.08)";
const OK_BORDER = "rgba(16,185,129,0.25)";

/**
 * Validation error card — strip bleeds full-width inside the question card.
 * Matches the Validation-Flow-v2 design (the amber-toned strip with the
 * red-outlined "Dismiss" pill below the action chips). Pure visual layer —
 * no behavior decisions made here; callbacks are wired by the orchestrator.
 */
export function ValidationErrorCard({
    error,
    status,
    onAddNarrative,
    onAddDiagnosis,
    onAcknowledge
}: Props) {
    if (status === "dismissed") {
        return <ResolvedStrip label="Acknowledged · dismissed" />;
    }
    if (status === "narrative-resolved") {
        return <ResolvedStrip label="Resolved via narrative" />;
    }
    if (status === "diagnosis-resolved") {
        return <ResolvedStrip label="Resolved via diagnosis" />;
    }

    const isHard = error.severity === "hard";

    return (
        <View style={ss.strip}>
            <View style={ss.headerRow}>
                <View style={ss.iconCircle}>
                    <MaterialCommunityIcons name="alert-outline" size={13} color="#fff" />
                </View>
                <View style={{ flex: 1 }}>
                    <View style={ss.tagRow}>
                        <Text style={ss.clinicalReviewLabel}>Clinical review</Text>
                        <View style={ss.dot} />
                        <View
                            style={[
                                ss.severityPill,
                                {
                                    backgroundColor: isHard ? ROSE_PILL_BG : WARN_PILL_BG
                                }
                            ]}
                        >
                            <Text style={[ss.severityPillText, { color: isHard ? ROSE : WARN_DK }]}>
                                {isHard ? "Required" : "Suggested"}
                            </Text>
                        </View>
                    </View>
                    <Text style={ss.messageText}>{error.message}</Text>
                </View>
            </View>

            <View style={ss.actionsRow}>
                <Pressable
                    onPress={onAddNarrative}
                    accessibilityRole="button"
                    accessibilityLabel="Go to narrative section"
                    style={({ pressed }) => [ss.primaryBtn, pressed && { opacity: 0.85 }]}
                >
                    <MaterialCommunityIcons name="microphone-outline" size={12} color="#fff" />
                    <Text style={ss.primaryText}>Go to narrative</Text>
                    <MaterialCommunityIcons name="arrow-right" size={11} color="#fff" />
                </Pressable>
                <Pressable
                    onPress={onAddDiagnosis}
                    accessibilityRole="button"
                    accessibilityLabel="Go to diagnoses section"
                    style={({ pressed }) => [ss.secondaryBtn, pressed && { opacity: 0.85 }]}
                >
                    <MaterialCommunityIcons name="plus" size={12} color={colors.ink} />
                    <Text style={ss.secondaryText}>Add diagnosis</Text>
                </Pressable>
            </View>

            <Pressable
                onPress={onAcknowledge}
                accessibilityRole="button"
                accessibilityLabel="Dismiss"
                style={({ pressed }) => [ss.dismissBtn, pressed && { opacity: 0.85 }]}
            >
                <MaterialCommunityIcons name="check" size={14} color={ROSE} />
                <Text style={ss.dismissText}>Dismiss</Text>
            </Pressable>
        </View>
    );
}

function ResolvedStrip({ label }: { label: string }) {
    return (
        <View style={ss.resolvedStrip}>
            <MaterialCommunityIcons name="check" size={13} color={OK} />
            <Text style={ss.resolvedText}>{label}</Text>
        </View>
    );
}

const ss = StyleSheet.create({
    strip: {
        backgroundColor: ROSE_BG,
        borderTopWidth: 1,
        borderTopColor: ROSE_BORDER,
        paddingVertical: 14,
        paddingHorizontal: 16
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
        marginBottom: 12
    },
    iconCircle: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: ROSE,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1,
        ...Platform.select({
            ios: {
                shadowColor: ROSE,
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.32,
                shadowRadius: 10
            },
            android: { elevation: 3 },
            default: {}
        })
    },
    tagRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginBottom: 4
    },
    clinicalReviewLabel: {
        fontFamily: fonts.extraBold,
        fontSize: 9.5,
        color: ROSE,
        letterSpacing: 0.6,
        textTransform: "uppercase"
    },
    dot: {
        width: 3,
        height: 3,
        borderRadius: 1.5,
        backgroundColor: ROSE
    },
    severityPill: {
        paddingVertical: 1,
        paddingHorizontal: 6,
        borderRadius: 4
    },
    severityPillText: {
        fontFamily: fonts.extraBold,
        fontSize: 9,
        letterSpacing: 0.4,
        textTransform: "uppercase"
    },
    messageText: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.ink,
        lineHeight: 19.5
    },
    actionsRow: {
        flexDirection: "row",
        gap: 8,
        marginLeft: 34,
        marginBottom: 12
    },
    primaryBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 9,
        paddingHorizontal: 14,
        borderRadius: 999,
        backgroundColor: colors.ink
    },
    primaryText: {
        fontFamily: fonts.extraBold,
        fontSize: 12,
        color: "#fff",
        letterSpacing: 0.3
    },
    secondaryBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 999,
        backgroundColor: "#fff",
        borderWidth: 1.5,
        borderColor: colors.border
    },
    secondaryText: {
        fontFamily: fonts.bold,
        fontSize: 12,
        color: colors.ink
    },
    dismissBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderRadius: 14,
        backgroundColor: "#fff",
        borderWidth: 1.5,
        borderColor: ROSE
    },
    dismissText: {
        fontFamily: fonts.extraBold,
        fontSize: 13.5,
        color: ROSE,
        letterSpacing: -0.2
    },
    resolvedStrip: {
        backgroundColor: OK_BG,
        borderTopWidth: 1,
        borderTopColor: OK_BORDER,
        paddingVertical: 10,
        paddingHorizontal: 16,
        flexDirection: "row",
        alignItems: "center",
        gap: 8
    },
    resolvedText: {
        fontFamily: fonts.extraBold,
        fontSize: 11,
        color: OK_DK,
        letterSpacing: 0.4,
        textTransform: "uppercase"
    }
});
